import React, { useState, useEffect } from 'react';
import { Users, User, Shield, Gear, ArrowClockwise } from '@phosphor-icons/react';
import { getAuthToken, getUserFromToken } from '../../../context/authContext';
import { Copy, Check } from '@phosphor-icons/react';
/**
 * Active Users View Component
 * Displays a detailed list of currently active users in the system
 */
const ActiveUsersView = () => {
    const [activeUsers, setActiveUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [copiedPhone, setCopiedPhone] = useState(null);

    // Obtener información del usuario actual
    useEffect(() => {
        const token = getAuthToken();

        if (token) {
            const userFromToken = getUserFromToken();
            if (userFromToken) {
                setCurrentUser({
                    id: userFromToken.id,
                    email: userFromToken.email,
                    role: userFromToken.role
                });
            }
        }
    }, []);

    const updateUserActivity = async () => {
        try {
            await fetch('/api/audit/update-activity', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    currentPage: window.location.pathname
                })
            });
        } catch (error) {
            console.error('Error updating user activity:', error);
        }
    };

    const fetchActiveUsers = async (showRefreshIndicator = false) => {
        if (showRefreshIndicator) {
            setRefreshing(true);
        }

        try {
            await updateUserActivity();
            const response = await fetch('/api/audit/active-users');

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const usersResponse = await response.json();

            setActiveUsers(usersResponse);
        } catch (error) {
            console.error('Error fetching active users:', error);
        } finally {
            setLoading(false);
            if (showRefreshIndicator) {
                setTimeout(() => setRefreshing(false), 500);
            }
        }
    };


    const handleCopyPhone = async (phoneNumber) => {
        if (phoneNumber) {
            try {
                if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                    await navigator.clipboard.writeText(phoneNumber);
                    setCopiedPhone(phoneNumber);
                    setTimeout(() => setCopiedPhone(null), 2000);
                } else {
                    // Fallback para navegadores sin clipboard API (ej: producción con HTTPS mal configurado)
                    const textArea = document.createElement('textarea');
                    textArea.value = phoneNumber;
                    textArea.style.position = 'fixed';
                    textArea.style.opacity = '0';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    
                    try {
                        document.execCommand('copy');
                        setCopiedPhone(phoneNumber);
                        setTimeout(() => setCopiedPhone(null), 2000);
                    } catch (err) {
                        console.error('Error al copiar número (fallback):', err);
                    }
                    document.body.removeChild(textArea);
                }
            } catch (err) {
                console.error('Error al copiar número:', err);
            }
        }
    };
    // Registrar actividad periódicamente
    useEffect(() => {
        if (currentUser) {
            updateUserActivity();
            const activityInterval = setInterval(updateUserActivity, 30000);
            return () => clearInterval(activityInterval);
        }
    }, [currentUser]);

    // Cargar usuarios y actualizar automáticamente
    useEffect(() => {
        fetchActiveUsers();
        const interval = setInterval(() => fetchActiveUsers(false), 5000);
        return () => clearInterval(interval);
    }, []);

    const getRoleIcon = (role) => {
        switch (role) {
            case 'admin':
                return <Shield className="w-5 h-5 text-error" weight="fill" />;
            case 'operator':
                return <Gear className="w-5 h-5 text-info" weight="fill" />;
            case 'technician':
                return <User className="w-5 h-5 text-warning" weight="fill" />;
            default:
                return <User className="w-5 h-5 text-muted-deep" />;
        }
    };

    const getRoleName = (role) => {
        switch (role) {
            case 'admin':
                return 'Administrador';
            case 'operator':
                return 'Operador';
            case 'technician':
                return 'Técnico';
            default:
                return role;
        }
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'admin':
                return 'bg-error/12 text-error border-error';
            case 'operator':
                return 'bg-info/12 text-info border-info';
            case 'technician':
                return 'bg-warning/12 text-warning border-warning';
            default:
                return 'bg-muted-deep/12 text-muted-deep border-muted-deep';
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Ahora mismo';
        if (diffMins < 60) return `Hace ${diffMins} min`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `Hace ${diffHours}h ${diffMins % 60}min`;

        const diffDays = Math.floor(diffHours / 24);
        return `Hace ${diffDays}d ${diffHours % 24}h`;
    };

    const getUserStatus = (dateString, lastEndpoint) => {
        // Si el último endpoint fue logout, mostrar Desconectado
        if (lastEndpoint === '/logout' || lastEndpoint === '/api/audit/logout') {
            return 'Desconectado';
        }

        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        return diffMins < 1 ? 'En línea' : 'Ausente';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'En línea':
                return { text: 'text-success', bg: 'bg-success' };
            case 'Ausente':
                return { text: 'text-warning', bg: 'bg-warning' };
            case 'Desconectado':
                return { text: 'text-error', bg: 'bg-error' };
            default:
                return { text: 'text-content-muted', bg: 'bg-muted-deep' };
        }
    };

    const getTimeColor = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'text-success';
        return 'text-warning';
    };

    // Mostrar todos los usuarios activos (incluyendo al usuario actual)
    const filteredUsers = activeUsers;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber mx-auto mb-4"></div>
                    <p className="text-content-muted">Cargando usuarios activos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className="p-3 bg-amber/12 rounded-lg">
                        <Users className="w-6 h-6 text-accent-text" weight="fill" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-content">Usuarios Activos</h2>
                        <p className="text-content-muted text-sm">
                            {filteredUsers.length} {filteredUsers.length === 1 ? 'usuario conectado' : 'usuarios conectados'}
                        </p>
                    </div>
                </div>

                <button
                    onClick={() => fetchActiveUsers(true)}
                    disabled={refreshing}
                    className="flex items-center space-x-2 px-4 py-2 bg-amber hover:bg-amber-hover text-on-amber rounded-lg transition-colors disabled:opacity-50"
                >
                    <ArrowClockwise className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    <span>Actualizar</span>
                </button>
            </div>

            {/* User List */}
            {filteredUsers.length === 0 ? (
                <div className="bg-surface rounded-xl shadow-md border border-line p-12 text-center">
                    <Users className="w-16 h-16 text-muted-deep mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-content mb-2">No hay usuarios conectados</h3>
                    <p className="text-content-muted">No se encontraron usuarios activos en este momento</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredUsers.map((user) => (
                        <div
                            key={user.email}
                            className="bg-surface rounded-xl shadow-md border border-line p-6 hover:border-amber transition-colors"
                        >
                            {/* User Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-surface-2 rounded-lg">
                                        {getRoleIcon(user.role)}
                                    </div>
                                    <div>
                                        <h3 className="text-content font-semibold">{user.name}</h3>
                                        <p className="text-content-muted text-sm">{user.email}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Role Badge */}
                            <div className="mb-4">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                                    {getRoleName(user.role)}
                                </span>
                            </div>
                            {user?.phone && (
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="text-content-muted">{user.phone}</p>
                                        {copiedPhone === user.phone ? (
                                            <Check size={20} className="text-success" />
                                        ) : (
                                            <Copy size={20} className="text-content cursor-pointer hover:text-content-muted transition-colors" onClick={() => handleCopyPhone(user.phone)} />
                                        )}
                                    </div>
                                </div>
                            )}
                            {!user?.phone && (
                                <p className="text-sm font-medium text-content">Teléfono: Sin teléfono asignado</p>
                            )}

                            {/* Activity Info */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-content-muted">Última actividad:</span>
                                    <span className={`font-medium ${getTimeColor(user.lastActivity)}`}>
                                        {formatTime(user.lastActivity)}
                                    </span>
                                </div>

                                {user.currentPage && (
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-content-muted">Página actual:</span>
                                        <span className="text-content-muted font-medium truncate ml-2">
                                            {user.currentPage}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Status Indicator */}
                            <div className="mt-4 pt-4 border-t border-line">
                                <div className="flex items-center space-x-2">
                                    <div className={`w-2 h-2 ${getStatusColor(getUserStatus(user.lastActivity, user.lastEndpoint)).bg} rounded-full ${getUserStatus(user.lastActivity, user.lastEndpoint) === 'En línea' ? 'animate-pulse' : ''}`}></div>
                                    <span className={`text-xs ${getStatusColor(getUserStatus(user.lastActivity, user.lastEndpoint)).text}`}>
                                        {getUserStatus(user.lastActivity, user.lastEndpoint)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ActiveUsersView;
